import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { OrthoPhotoDto } from 'src/app/bo/orthophoto';
import { SiteDxfAddFileModalComponent } from 'src/app/components/site-setup/site-dxf-add-file-modal/site-dxf-add-file-modal.component';
import { SiteOrthophotosAddFilesModalComponent } from 'src/app/components/site-setup/site-orthophotos-add-files-modal/site-orthophotos-add-files-modal.component';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { SiteMapStore } from '../../site-map.store';

interface LayerRow {
  key: number;
  name: string;
  date: Date | null;
  visible: boolean;
  zOrder: number;
}

/** Liste des couches d'un site (orthophotos ou DXF), reprise de l'ancienne carte : visibilité, ajout, et pour les orthophotos ordre et suppression. */
@Component({
  selector: 'app-site-map-layers-panel',
  standalone: true,
  templateUrl: './layers-panel.component.html',
  styleUrls: ['./layers-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, DatePipe, DragDropModule, PopupComponent, ButtonComponent],
})
export class SiteMapLayersPanelComponent {
  readonly kind = input.required<'orthophotos' | 'dxf'>();

  protected readonly store = inject(SiteMapStore);
  private readonly modalService = inject(BsModalService);
  private readonly keycloak = inject(KeycloakUtilisateurService);
  private readonly fonctions = inject(FonctionsService);

  protected readonly orthophotoToDelete = signal<OrthoPhotoDto | null>(null);

  /** Mêmes rôles que l'ancienne carte pour l'ajout et la suppression de fichiers. */
  protected readonly canManage = ['sys_admin', 'manage_site'].some(role => this.keycloak.hasRole(role));

  protected readonly icon = computed(() => (this.kind() === 'orthophotos' ? 'assets/icon/ortho.svg' : 'assets/icon/Polygon.svg'));
  protected readonly title = computed(() => (this.kind() === 'orthophotos' ? 'Orthophoto' : 'DXF'));
  protected readonly emptyKey = computed(() => (this.kind() === 'orthophotos' ? 'ORTHOPHOTOS.ERREUR_NOT_FOUND' : 'DXF.NOT_FOUND'));

  private readonly sortedOrthophotos = computed(() => [...this.store.orthophotos()].sort((a, b) => a.zOrder - b.zOrder));

  protected readonly rows = computed<LayerRow[]>(() => {
    if (this.kind() === 'orthophotos') {
      return this.sortedOrthophotos().map((item, index) => ({
        key: item.id,
        name: item.mapName || `Couche ${index + 1}`,
        date: item.creationDate ?? null,
        visible: item.isVisible,
        zOrder: item.zOrder,
      }));
    }
    return this.store.dxfLayers().map((item, index) => ({
      key: item.id,
      name: item.name || `DXF ${index + 1}`,
      date: null,
      visible: item.isVisible,
      zOrder: index + 1,
    }));
  });

  /** Ouvre la fenêtre d'envoi de fichier de l'ancienne carte, puis recharge la liste à sa fermeture. */
  protected add(): void {
    if (this.kind() === 'orthophotos') {
      const modal = this.modalService.show(SiteOrthophotosAddFilesModalComponent, {
        initialState: { window: 1, multi: false, types: ['.tif', '.tiff', '.geotiff'] },
        class: 'custome-orto-modal modal-lg',
        backdrop: 'static',
        keyboard: false,
      });
      modal.onHide?.subscribe((result: string) => {
        if (result !== 'cancel') this.store.reloadOrthophotos();
      });
      return;
    }

    const siteId = this.store.siteId();
    if (siteId === null) return;
    const modal = this.modalService.show(SiteDxfAddFileModalComponent, {
      initialState: { siteId },
      class: 'modal-md',
      backdrop: 'static',
      keyboard: false,
    });
    modal.onHide?.subscribe(() => this.store.reloadDxfLayers());
  }

  protected toggle(key: number): void {
    if (this.kind() === 'orthophotos') {
      const orthophoto = this.store.orthophotos().find(item => item.id === key);
      if (orthophoto) this.store.toggleOrthophoto(orthophoto);
      return;
    }
    const polygone = this.store.dxfLayers().find(item => item.id === key);
    if (polygone) this.store.toggleDxfLayer(polygone);
  }

  protected move(key: number, delta: -1 | 1): void {
    const layers = [...this.sortedOrthophotos()];
    const from = layers.findIndex(item => item.id === key);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= layers.length) return;
    moveItemInArray(layers, from, to);
    this.applyOrder(layers);
  }

  protected drop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    const layers = [...this.sortedOrthophotos()];
    moveItemInArray(layers, event.previousIndex, event.currentIndex);
    this.applyOrder(layers);
  }

  protected askDelete(key: number): void {
    const orthophoto = this.store.orthophotos().find(item => item.id === key);
    if (orthophoto) this.orthophotoToDelete.set(orthophoto);
  }

  protected cancelDelete(): void {
    this.orthophotoToDelete.set(null);
  }

  protected confirmDelete(): void {
    const orthophoto = this.orthophotoToDelete();
    if (!orthophoto) return;

    this.fonctions.loading_screen(true);
    this.store.deleteOrthophoto(orthophoto).subscribe({
      next: result => {
        this.fonctions.loading_screen(false);
        if ((result?.statusCode ?? 200) > 299) {
          this.fonctions.launch_toast(false, false, result.message);
        } else {
          this.store.removeOrthophoto(orthophoto.id);
          this.fonctions.launch_toast(true);
        }
        this.orthophotoToDelete.set(null);
      },
      error: () => {
        this.fonctions.loading_screen(false);
        this.fonctions.launch_toast(false);
        this.orthophotoToDelete.set(null);
      },
    });
  }

  private applyOrder(layers: OrthoPhotoDto[]): void {
    this.store.reorderOrthophotos(layers).subscribe({
      next: result => {
        if ((result?.statusCode ?? 200) > 299) this.fonctions.launch_toast(false, false, result?.message);
        else if (result) this.fonctions.launch_toast(true);
      },
      error: () => this.fonctions.launch_toast(false),
    });
  }

}
